import { type CreateAndUploadMemoReq, FileObject, Memo } from '@kalos/kalos-rpc';
import { useCallback, useState } from 'react';

import {
  useCreateFullMemoMutation,
  useUpdateMemoMutation,
} from '../../../../../hooks/react-query/useMemoQuery';
import {
  isVideoFile,
  shouldUseMultipartUpload,
  useLargeFileUploadMutation,
} from '../../../../../hooks/react-query/useS3';
import {
  audioToFileObject,
  fileToFileObject,
  fileToFileObjectHeader,
  imageFileToStagedFileObject,
  isImageForStaging,
  MEMO_STAGING_PREPARE_CONCURRENCY,
  mergeFileIdCsv,
  runWithConcurrency,
} from '../../../../../tools/fileUploadUtils';
import { easternTimestamp } from '../../../../../tools/helpers';

export interface SubmitJobNoteInput {
  description: string;
  classificationId: number;
  files: File[];
  audioBlob?: Blob | null;
  isPinned?: boolean;
  isInternal?: boolean;
  jobId?: number;
  loggedUserId: number;
  diagnosisId?: number;
}

/**
 * Creates a job note: downscales images, inlines audio, creates the memo, then uploads any
 * large videos separately and patches them onto the created memo. Shared by the note form
 * and the overview Notes component so both handle big files the same way.
 */
export const useSubmitJobNote = () => {
  const { mutateAsync: createMemo, isPending } = useCreateFullMemoMutation();
  const { mutateAsync: updateMemo } = useUpdateMemoMutation();
  const { mutateAsync: uploadLargeFile, isPending: isLargeFileUploading } =
    useLargeFileUploadMutation();

  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [isUploadingLargeVideos, setIsUploadingLargeVideos] = useState(false);
  const [isUpdatingMemo, setIsUpdatingMemo] = useState(false);

  const resetProgress = useCallback(() => setUploadProgress({}), []);

  const submitNote = useCallback(
    async ({
      description,
      classificationId,
      files,
      audioBlob = null,
      isPinned = false,
      isInternal = true,
      jobId,
      loggedUserId,
      diagnosisId,
    }: SubmitJobNoteInput) => {
      // Separate files into regular and large video
      const regularFiles: File[] = [];
      const largeVideoFiles: File[] = [];

      files.forEach((file) => {
        if (isVideoFile(file) && shouldUseMultipartUpload(file)) {
          largeVideoFiles.push(file);
        } else {
          regularFiles.push(file);
        }
      });

      // Downscale raster images before embedding in the gRPC-web request; PDFs and small
      // videos take the raw path. Inline full-resolution images can blow past proxy size
      // limits on CreateFullMemo (the 503 reported by QA).
      const imageFileObjects = await runWithConcurrency(
        regularFiles,
        MEMO_STAGING_PREPARE_CONCURRENCY,
        (f) => (isImageForStaging(f) ? imageFileToStagedFileObject(f) : fileToFileObject(f)),
      );

      const audioFileObject = audioBlob ? await audioToFileObject(audioBlob) : undefined;
      const allFileObjects = [...imageFileObjects, ...(audioFileObject ? [audioFileObject] : [])];

      const request: CreateAndUploadMemoReq = {
        memo: Memo.create({
          description,
          createdBy: loggedUserId,
          userId: loggedUserId,
          isActive: true,
          statusId: 1,
          eventId: jobId,
          createdDate: easternTimestamp(),
          classificationId,
          isPinned,
          isInternal,
          ...(diagnosisId && { diagnosisId }),
          fieldMask: ['IsInternal'],
        }),
        files: {
          files: allFileObjects,
        },
      };

      const createdMemo = await createMemo(request);

      // Handle large video files after memo creation
      if (largeVideoFiles.length > 0 && createdMemo?.id) {
        setIsUploadingLargeVideos(true);

        const uploadedFileObjects: FileObject[] = [];

        for (const file of largeVideoFiles) {
          const { uniqueName, mimeType: enhancedMime } = await fileToFileObjectHeader(file);

          const uploadProtoFile = await uploadLargeFile({
            file,
            bucket: 'testbuckethelios',
            key: uniqueName,
            onProgress: (progress) => {
              setUploadProgress((prev) => ({ ...prev, [uniqueName]: progress }));
            },
          }).catch((error) => {
            console.error('Multipart upload failed (post-memo):', error);
            throw error;
          });

          uploadedFileObjects.push(
            FileObject.create({
              id: uploadProtoFile.id,
              bucket: uploadProtoFile.bucket,
              key: uploadProtoFile.name || uniqueName,
              mimeType: enhancedMime || uploadProtoFile.mimeType || file.type,
              data: new Uint8Array(0),
            }),
          );
        }

        const newFileIds = uploadedFileObjects.map((f) => f.id).filter(Boolean);

        if (newFileIds.length > 0) {
          setIsUpdatingMemo(true);
          const updatedCsv = mergeFileIdCsv(createdMemo.fileId || '', newFileIds);

          await updateMemo(
            Memo.create({
              id: createdMemo.id,
              fileId: updatedCsv,
              fieldMask: ['FileId'],
            }),
          );
          setIsUpdatingMemo(false);
        }

        setIsUploadingLargeVideos(false);
      }

      return createdMemo;
    },
    [createMemo, updateMemo, uploadLargeFile],
  );

  return {
    submitNote,
    resetProgress,
    uploadProgress,
    isPending,
    isLargeFileUploading,
    isUploadingLargeVideos,
    isUpdatingMemo,
  };
};
